import { useMemo, useState, type JSX } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  type DataTableColumn,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePlatformOwner } from "../../../access/platform-owner.js";
import { ApiError, apiGet, apiSend } from "../../../lib/api.js";
import { formatTimestampSafe } from "../../../locale-format.js";
import { useSession } from "../../../session/SessionProvider.js";
import { ControlNote } from "../control/ControlPrimitives.js";

/**
 * E-GTCC-MODUL-ENTZUG1 (PASS 003586) - "Module je Mandant" im GTCC.
 *
 * Das Wort des Owners vom 2026-10-23 um 01:05: beim Sammeldeploy bekommen
 * alle Bestandsmandanten alle Module manuell frei, danach schaltet der Owner
 * die nicht gebuchten Module selbst ab. Lane I baut den Freigabe-Lauf,
 * diese Seite ist der Weg zurueck: sie zeigt je Mandant jedes Modul mit
 * seinem Status und - die entscheidende Spalte - ob es laut Paket GEBUCHT
 * ist oder nur manuell offen steht. Ohne diese Spalte muesste der Owner
 * raten, welche Freigabe er abschalten darf.
 *
 * Gemessen vor dem Bau (Paket 003586, Schritt 1): im GTCC gab es ueberhaupt
 * keine Modulsicht je Mandant - die Suche nach module-grants, moduleGrant
 * und billing-modules ueber apps/admin/src hatte null Treffer, und
 * GET /v1/tenancy/modules liest den AMBIENTEN Mandanten und liefert keine
 * Grant-Ids. Deshalb kam mit diesem Paket der Lesevertrag
 * GET /v1/admin/tenants/:tenantId/module-grants dazu
 * (services/api/src/modules/billing-modules/module-grants.routes.ts).
 *
 * Drei Entscheidungen, die man hier sehen soll:
 *
 * 1. Der Rollen-Riegel steht doppelt. Die Route prueft
 *    requirePlatformOwner("tenants"); diese View prueft dieselbe Regel noch
 *    einmal selbst (usePlatformOwner), damit eine Sitzung ohne die Rolle
 *    einen Satz liest statt eines 403-Fehlers. Der Server bleibt die
 *    Wahrheit.
 * 2. Abschalten heisst "Grant beenden", nicht "Zeile loeschen". Der Ledger
 *    ist append-only; POST .../module-grants/:grantId/end setzt ends_at und
 *    schreibt die Audit-Zeile. Danach ist das Modul 90 Tage nur lesbar
 *    (Owner-Entscheidung 3) - das steht als Satz unter der Tabelle, damit
 *    niemand ein sofortiges Verschwinden erwartet.
 * 3. Jede Abschaltung braucht zwei Klicks. Der erste zeigt die Bestaetigung
 *    mit Modulnamen UND Mandantennamen, der zweite schickt sie ab -
 *    gleiche Form wie der Grant-Widerruf in
 *    views/pgrant/GtccPlatformGrantsView.tsx.
 *
 * Mehrere offene Freigaben je Modul sind moeglich (der Ledger verbietet sie
 * nicht fuer unterschiedliche Quellen). Der Knopf beendet die JUENGSTE
 * offene Freigabe; bleibt danach eine weitere offen, erscheint er erneut,
 * statt dass die Seite im Stillen mehrere Schreibvorgaenge buendelt.
 */

/** ModuleDisplayStatus aus services/api/src/modules/billing-modules/module-view.ts. */
type ModuleStatus = "included" | "active" | "trial" | "read_only" | "not_booked";

/** ModuleGrantSource aus services/api/src/modules/billing-modules/effective-modules.ts. */
type GrantSource = "plan" | "trial" | "manual";

/** GrantLifecycle aus services/api/src/modules/billing-modules/effective-modules.ts. */
type GrantLifecycle = "scheduled" | "open" | "ended";

interface GrantRow {
  readonly id: string;
  readonly source: GrantSource;
  readonly startsAt: string;
  readonly endsAt: string | null;
  readonly reason: string | null;
  readonly lifecycle: GrantLifecycle;
  readonly open: boolean;
}

interface ModuleRow {
  readonly module: string;
  readonly sellable: boolean;
  readonly status: ModuleStatus;
  readonly source: GrantSource | null;
  readonly planBooked: boolean;
  readonly endsAt: string | null;
  readonly readOnlyUntil: string | null;
  readonly grants: readonly GrantRow[];
}

interface ModuleOverview {
  readonly tenantId: string;
  readonly now: string;
  readonly plan: string;
  readonly modules?: readonly ModuleRow[];
  /** Ledger-Lesung gekappt: aeltere Freigaben fehlen in grants. */
  readonly truncated?: boolean;
  /** Die Kappungsgrenze, damit der Hinweis die Zahl nennen kann. */
  readonly grantCap?: number;
}

interface TenantRowDto {
  readonly id: string;
  readonly displayName: string | null;
}

interface TenantsListResponse {
  readonly items?: readonly TenantRowDto[];
}

interface EndGrantVars {
  readonly tenantId: string;
  readonly grantId: string;
  readonly module: string;
}

/**
 * Die Modulnamen kommen aus dem geteilten Katalog, den auch die
 * Mandantenseite "Module und Paket" liest - eine Uebersetzung, zwei Masken.
 * clean_city stand dort bis zu diesem Paket nur unter "Kommt spaeter"; der
 * Name modules.clean_city.name ist mit 003586 dazugekommen, weil das Modul
 * laengst im Katalog verkauft wird.
 */
function moduleNameKey(module: string): string {
  return `modules.${module}.name`;
}

const STATUS_KEYS: Readonly<Record<ModuleStatus, string>> = {
  included: "modules.status.included",
  active: "modules.status.active",
  trial: "modules.status.trial",
  read_only: "modules.status.read_only",
  not_booked: "modules.status.not_booked",
};

const SOURCE_KEYS: Readonly<Record<GrantSource, string>> = {
  plan: "modules.source.plan",
  trial: "modules.source.trial",
  manual: "modules.source.manual",
};

/** Die { error: "<code>" }-Koerper, die module-grants.routes.ts schickt. */
const END_ERROR_KEYS: Readonly<Record<string, string>> = {
  grant_not_found: "admin.gtcc.tmod.end.error.gone",
  grant_already_ended: "admin.gtcc.tmod.end.error.already_ended",
  grant_not_started: "admin.gtcc.tmod.end.error.not_started",
  tenant_not_found: "admin.gtcc.tmod.end.error.tenant_gone",
};

function isForbidden(error: Error): boolean {
  return error instanceof ApiError && error.status === 403;
}

function apiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body: unknown = error.body;
  if (typeof body !== "object" || body === null) return undefined;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
}

/**
 * Die juengste LAUFENDE Freigabe - genau die, die ein Abschalten beendet.
 *
 * `open` ist seit Review H (003653) genau das, was die API akzeptiert:
 * begonnen und nicht beendet (grantLifecycle in
 * services/api/src/modules/billing-modules/effective-modules.ts). Vorher
 * zaehlte auch eine erst naechsten Monat beginnende Freigabe als offen, und
 * diese Seite zeigte einen Abschalten-Knopf, den die Route mit
 * grant_not_started ablehnte.
 */
export function endableGrant(row: ModuleRow): GrantRow | null {
  const open = row.grants.filter((grant) => grant.open);
  if (open.length === 0) return null;
  return open.reduce((best, grant) => (grant.startsAt > best.startsAt ? grant : best));
}

/**
 * Die NAECHSTE geplante Freigabe - begonnen hat sie noch nicht.
 *
 * Eigener Zustand statt eines Knopfes: abschalten kann man sie nicht (die
 * Tabellenbedingung tenant_module_grants_window_chk verbietet
 * ends_at <= starts_at), verschweigen soll die Seite sie aber auch nicht,
 * sonst wirkt ein eingetragener Starttermin wie ein Fehler. Die fruehste
 * zaehlt, weil sie als erste wirksam wird.
 */
export function scheduledGrant(row: ModuleRow): GrantRow | null {
  const planned = row.grants.filter((grant) => grant.lifecycle === "scheduled");
  if (planned.length === 0) return null;
  return planned.reduce((first, grant) => (grant.startsAt < first.startsAt ? grant : first));
}

/** Offen, aber nicht gebucht: die Liste, die der Owner abarbeiten will. */
export function unbookedOpenModules(rows: readonly ModuleRow[]): readonly ModuleRow[] {
  return rows.filter(
    (row) => !row.planBooked && (row.status === "active" || row.status === "trial"),
  );
}

const selectStyle = {
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
  color: "var(--gt-sys-text)",
  padding: "var(--gt-sys-space-2)",
  inlineSize: "100%",
} as const;

function FieldLabel({
  children,
  text,
}: {
  readonly children: JSX.Element;
  readonly text: string;
}): JSX.Element {
  return (
    <label style={{ display: "block" }}>
      <Stack gap={1}>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {text}
        </span>
        {children}
      </Stack>
    </label>
  );
}

export function GtccTenantModulesView({
  hideHeader = false,
}: { readonly hideHeader?: boolean } = {}): JSX.Element {
  const { t, locale } = useLocale();
  const session = useSession();
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState("");
  const [confirmModule, setConfirmModule] = useState<string | null>(null);

  const isOwner = usePlatformOwner(session);

  const tenantsQuery = useQuery<TenantsListResponse, Error>({
    queryKey: ["gtcc", "tmod", "tenants"],
    queryFn: ({ signal }) => apiGet<TenantsListResponse>("/v1/admin/tenants", undefined, signal),
    enabled: isOwner,
    staleTime: 60_000,
  });

  const overviewQuery = useQuery<ModuleOverview, Error>({
    queryKey: ["gtcc", "tmod", "overview", tenantId],
    queryFn: ({ signal }) =>
      apiGet<ModuleOverview>(`/v1/admin/tenants/${tenantId}/module-grants`, undefined, signal),
    enabled: isOwner && tenantId !== "",
    staleTime: 15_000,
  });

  const endMutation = useMutation<unknown, Error, EndGrantVars>({
    mutationFn: (vars) =>
      apiSend<unknown>(
        "POST",
        `/v1/admin/tenants/${vars.tenantId}/module-grants/${vars.grantId}/end`,
        {},
      ),
    onSettled: () => {
      setConfirmModule(null);
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "tmod", "overview"] });
    },
  });

  const tenants = useMemo(() => tenantsQuery.data?.items ?? [], [tenantsQuery.data]);
  // Review F zu #3270: die Rueckfrage nannte nur das Modul. Wer zwei
  // Mandanten nebeneinander bearbeitet, soll vor dem zweiten Klick lesen,
  // WEN er abschaltet - deshalb steht der Mandantenname mit in der Frage,
  // mit der Id als Rueckfall, wenn die Liste keinen Namen fuehrt.
  const tenantLabel = useMemo(
    () => tenants.find((tenant) => tenant.id === tenantId)?.displayName ?? tenantId,
    [tenants, tenantId],
  );
  const rows = useMemo(() => overviewQuery.data?.modules ?? [], [overviewQuery.data]);
  const unbooked = useMemo(() => unbookedOpenModules(rows), [rows]);
  const grantCap = overviewQuery.data?.grantCap ?? 0;

  const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });
  const dateOnly = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });

  function stamp(fmt: Intl.DateTimeFormat, iso: string): JSX.Element {
    return <time dateTime={iso}>{formatTimestampSafe(iso, (d) => fmt.format(d))}</time>;
  }

  const columns: ReadonlyArray<DataTableColumn<ModuleRow>> = [
    {
      id: "module",
      header: t("admin.gtcc.tmod.col.module"),
      cell: (row) => t(moduleNameKey(row.module)),
    },
    {
      id: "status",
      header: t("admin.gtcc.tmod.col.status"),
      cell: (row) => t(STATUS_KEYS[row.status]),
    },
    {
      id: "booked",
      header: t("admin.gtcc.tmod.col.booked"),
      cell: (row) =>
        row.planBooked
          ? t("admin.gtcc.tmod.booked.plan")
          : row.status === "active" || row.status === "trial"
            ? t("admin.gtcc.tmod.booked.manual")
            : t("admin.gtcc.tmod.booked.no"),
    },
    {
      id: "source",
      header: t("admin.gtcc.tmod.col.source"),
      cell: (row) => (row.source === null ? "-" : t(SOURCE_KEYS[row.source])),
    },
    {
      id: "period",
      header: t("admin.gtcc.tmod.col.period"),
      cell: (row) => {
        const grant = endableGrant(row);
        if (row.status === "read_only" && row.readOnlyUntil !== null) {
          return (
            <>
              {t("admin.gtcc.tmod.read_only_until")} {stamp(dateOnly, row.readOnlyUntil)}
            </>
          );
        }
        if (grant === null) {
          const planned = scheduledGrant(row);
          if (planned === null) return "-";
          return (
            <>
              {t("admin.gtcc.tmod.scheduled_from")} {stamp(dateTime, planned.startsAt)}
            </>
          );
        }
        return (
          <>
            {t("admin.gtcc.tmod.since")} {stamp(dateTime, grant.startsAt)}
            {row.endsAt === null ? null : (
              <>
                {" - "}
                {t("admin.gtcc.tmod.until")} {stamp(dateOnly, row.endsAt)}
              </>
            )}
          </>
        );
      },
    },
    {
      id: "actions",
      header: t("admin.gtcc.tmod.col.actions"),
      cell: (row) => {
        const grant = endableGrant(row);
        if (grant === null) {
          // Geplante Freigabe: kein Knopf, aber ein eigener Satz. Ein Klick
          // waere hier nur eine 409-Antwort (grant_not_started).
          const planned = scheduledGrant(row);
          const note = planned
            ? t("admin.gtcc.tmod.not_started_yet")
            : row.sellable
              ? t("admin.gtcc.tmod.nothing_to_end")
              : t("admin.gtcc.tmod.base_note");
          return (
            <span
              style={{ color: "var(--gt-sys-text-muted)" }}
              data-testid={planned ? `gtcc-tmod-scheduled-${row.module}` : undefined}
            >
              {note}
            </span>
          );
        }
        if (confirmModule === row.module) {
          return (
            <Inline gap={2}>
              <Button
                variant="danger"
                size="sm"
                disabled={endMutation.isPending}
                onClick={() =>
                  endMutation.mutate({ tenantId, grantId: grant.id, module: row.module })
                }
                data-testid={`gtcc-tmod-end-confirm-${row.module}`}
              >
                {t("admin.gtcc.tmod.end.confirm", {
                  module: t(moduleNameKey(row.module)),
                  tenant: tenantLabel,
                })}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmModule(null)}>
                {t("common.cancel")}
              </Button>
            </Inline>
          );
        }
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmModule(row.module)}
            data-testid={`gtcc-tmod-end-${row.module}`}
          >
            {t("admin.gtcc.tmod.end.action")}
          </Button>
        );
      },
    },
  ];

  const header = (
    <Stack gap={1}>
      <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
        {t("admin.gtcc.tmod.title")}
      </h2>
      <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
        {t("admin.gtcc.tmod.subtitle")}
      </p>
    </Stack>
  );

  // Dieselbe Regel, die die Route prueft, hier noch einmal in denselben
  // Worten: die ROLLE laesst herein, ein Grant nicht.
  if (!isOwner) {
    return (
      <Stack gap={4}>
        {hideHeader ? null : header}
        <div data-testid="gtcc-tmod-role-gate">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.tmod.forbidden.title")}
            body={t("admin.gtcc.tmod.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  // Die Mandantenliste hat ihren eigenen Fehlerzustand: faellt SIE aus, gibt
  // es nichts zu waehlen, und das Ledger wird nie gelesen. Ohne diesen Satz
  // stuende die Seite mit einer leeren Auswahl und dem Hinweis "waehlen Sie
  // einen Mandanten" da - der Owner wuerde einen Bedienfehler bei sich
  // suchen, statt den fehlgeschlagenen Lesevorgang zu sehen.
  const tenantsError = tenantsQuery.error;
  const listError = overviewQuery.error;
  const endError = endMutation.error;
  const endErrorCode = endError ? apiErrorCode(endError) : undefined;

  return (
    <Stack gap={4}>
      {hideHeader ? null : header}

      <section aria-label={t("admin.gtcc.tmod.tenant.section")}>
        <Card>
          <Stack gap={3}>
            <FieldLabel text={t("admin.gtcc.tmod.tenant.label")}>
              <select
                value={tenantId}
                onChange={(event) => {
                  setTenantId(event.target.value);
                  setConfirmModule(null);
                }}
                style={selectStyle}
                data-testid="gtcc-tmod-tenant-select"
              >
                <option value="">{t("admin.gtcc.tmod.tenant.placeholder")}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.displayName ?? tenant.id}
                  </option>
                ))}
              </select>
            </FieldLabel>
            {tenantsError ? (
              <Stack gap={2}>
                <div data-testid="gtcc-tmod-tenant-error">
                  <ErrorState
                    severity={isForbidden(tenantsError) ? "warn" : "error"}
                    title={
                      isForbidden(tenantsError)
                        ? t("admin.gtcc.tmod.forbidden.title")
                        : t("admin.gtcc.tmod.tenant.error.title")
                    }
                    body={
                      isForbidden(tenantsError)
                        ? t("admin.gtcc.tmod.forbidden.body")
                        : t("admin.gtcc.tmod.tenant.error.body")
                    }
                  />
                </div>
                {isForbidden(tenantsError) ? null : (
                  <Inline gap={2}>
                    <Button variant="secondary" onClick={() => void tenantsQuery.refetch()}>
                      {t("common.retry")}
                    </Button>
                  </Inline>
                )}
                {tenantsError instanceof ApiError && tenantsError.requestId ? (
                  <code>{t("common.error.request_id", { id: tenantsError.requestId })}</code>
                ) : null}
              </Stack>
            ) : null}
            {overviewQuery.data ? (
              <ControlNote testId="gtcc-tmod-plan">
                {t("admin.gtcc.tmod.plan", { plan: overviewQuery.data.plan })}
              </ControlNote>
            ) : null}
          </Stack>
        </Card>
      </section>

      <section aria-label={t("admin.gtcc.tmod.list.section")}>
        <Card>
          <Stack gap={3}>
            <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {t("admin.gtcc.tmod.list.section")}
            </h3>

            {endError ? (
              <div data-testid="gtcc-tmod-end-error">
                <ErrorState
                  severity={isForbidden(endError) ? "warn" : "error"}
                  title={t("admin.gtcc.tmod.end.error.title")}
                  body={t(
                    isForbidden(endError)
                      ? "admin.gtcc.tmod.forbidden.body"
                      : (END_ERROR_KEYS[endErrorCode ?? ""] ?? "admin.gtcc.tmod.end.error.unknown"),
                  )}
                />
              </div>
            ) : null}

            {tenantId === "" ? (
              <div data-testid="gtcc-tmod-no-tenant">
                <EmptyState
                  title={t("admin.gtcc.tmod.no_tenant.title")}
                  body={t("admin.gtcc.tmod.no_tenant.body")}
                />
              </div>
            ) : overviewQuery.isPending ? (
              <LoadingState label={t("admin.gtcc.tmod.loading")} />
            ) : listError ? (
              <Stack gap={2}>
                <div data-testid="gtcc-tmod-list-error">
                  <ErrorState
                    severity={isForbidden(listError) ? "warn" : "error"}
                    title={
                      isForbidden(listError)
                        ? t("admin.gtcc.tmod.forbidden.title")
                        : t("admin.gtcc.tmod.error.title")
                    }
                    body={
                      isForbidden(listError)
                        ? t("admin.gtcc.tmod.forbidden.body")
                        : t("admin.gtcc.tmod.error.body")
                    }
                  />
                </div>
                {isForbidden(listError) ? null : (
                  <Inline gap={2}>
                    <Button variant="secondary" onClick={() => void overviewQuery.refetch()}>
                      {t("common.retry")}
                    </Button>
                  </Inline>
                )}
                {listError instanceof ApiError && listError.requestId ? (
                  <code>{t("common.error.request_id", { id: listError.requestId })}</code>
                ) : null}
              </Stack>
            ) : rows.length === 0 ? (
              <div data-testid="gtcc-tmod-empty">
                <EmptyState
                  title={t("admin.gtcc.tmod.empty.title")}
                  body={t("admin.gtcc.tmod.empty.body")}
                />
              </div>
            ) : (
              <Stack gap={2}>
                <DataTable<ModuleRow>
                  caption={t("admin.gtcc.tmod.list.caption")}
                  columns={columns}
                  rows={rows}
                  getRowId={(row) => row.module}
                  testId="gtcc-tmod-table"
                  rowTestId={(row) => `gtcc-tmod-row-${row.module}`}
                />
                <ControlNote testId="gtcc-tmod-unbooked">
                  {unbooked.length === 0
                    ? t("admin.gtcc.tmod.unbooked.none")
                    : t("admin.gtcc.tmod.unbooked.some", {
                        count: String(unbooked.length),
                        modules: unbooked.map((row) => t(moduleNameKey(row.module))).join(", "),
                      })}
                </ControlNote>
                {/* Befund 2 aus Review H (003653): die Route liest das Ledger
                    gekappt und meldet die Kappung. Die Seite liest das Signal
                    hier unmittelbar, damit der Kappungs-Waechter die Stelle
                    sieht, statt eine halbe Historie als ganze zu zeigen. */}
                {overviewQuery.data?.truncated === true ? (
                  <ControlNote testId="gtcc-tmod-truncated">
                    {t("admin.gtcc.tmod.truncated", { count: String(grantCap) })}
                  </ControlNote>
                ) : null}
                <ControlNote testId="gtcc-tmod-grace">
                  {t("admin.gtcc.tmod.grace_note")}
                </ControlNote>
              </Stack>
            )}
          </Stack>
        </Card>
      </section>

      <p
        data-testid="gtcc-tmod-scope-note"
        style={{
          margin: 0,
          color: "var(--gt-sys-text-muted)",
          fontSize: "var(--gt-sys-font-size-xs)",
        }}
      >
        {t("admin.gtcc.tmod.scope_note")}
      </p>
    </Stack>
  );
}
